/**
 * Monitoramento de quota e eviction sob pressão.
 */

import { recordEvicted, recordStorageBytes, recordPartitionEviction } from '../telemetry';
import { getQueuePartition } from '../queue/partitioning';
import type { QueueItem } from '../queue';

const DROPPABLE = new Set([
  'BEHAVIOR_SUMMARY',
  'SCROLL',
  'PAGE_CLICK',
  'ELEMENT_VISIBLE',
  'ELEMENT_ENTER',
  'ELEMENT_EXIT',
]);

const PROTECTED_TYPES = new Set([
  'PURCHASE',
  'LEAD',
  'CHECKOUT',
  'PAYMENT',
  'INITIATE_CHECKOUT',
  'ADD_PAYMENT_INFO',
]);

export async function estimateStorageUsage(items: QueueItem[]): Promise<number> {
  const bytes = new TextEncoder().encode(JSON.stringify(items)).length;
  recordStorageBytes(bytes);
  return bytes;
}

export async function getStorageQuota(): Promise<{
  usage?: number;
  quota?: number;
}> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
    return {};
  }
  try {
    const est = await navigator.storage.estimate();
    return { usage: est.usage, quota: est.quota };
  } catch {
    return {};
  }
}

/** Remove itens descartáveis de menor prioridade até caber no limite. */
export function evictLowPriorityItems(
  items: QueueItem[],
  maxItems: number
): QueueItem[] {
  if (items.length <= maxItems) return items;
  const protectedItems = items.filter(i => PROTECTED_TYPES.has(i.params.type));
  const evictable = items.filter(
    i => !PROTECTED_TYPES.has(i.params.type) && DROPPABLE.has(i.params.type)
  );
  const middle = items.filter(
    i => !PROTECTED_TYPES.has(i.params.type) && !DROPPABLE.has(i.params.type)
  );
  let result = [...protectedItems, ...middle, ...evictable];
  if (result.length <= maxItems) return result;
  const toRemove = result.length - maxItems;
  const removed: QueueItem[] = [];
  while (removed.length < toRemove && evictable.length > 0) {
    const item = evictable.shift()!;
    removed.push(item);
    recordPartitionEviction(getQueuePartition(item));
  }
  result = [...protectedItems, ...middle, ...evictable];
  if (result.length > maxItems) {
    result = result.slice(0, maxItems);
  }
  recordEvicted(removed.length);
  return result;
}
